/**
 * Formik-bound controls for the auth screens.
 *
 * All of them are built on `FastField`, not `Field`. That is the whole
 * render story: Formik keeps one state object for the form, so with plain
 * `Field` every keystroke in any input re-renders every other input, its
 * antd wrapper and its dropdown. `FastField` implements `shouldComponentUpdate`
 * and re-renders only when ITS value, error or touched flag changes — or when
 * its own props change, which is what lets the phone field still react to a
 * country switch.
 *
 * The forms pass `<Form>` children rather than a render prop for the same
 * reason: static chrome (headings, fine print, the channel toggle) then sits
 * outside the re-rendering path entirely.
 */

import { memo } from "react";
import { FastField } from "formik";
import { Button, Input, Select } from "antd";

export { default as AddressField } from "./AddressField";

const fieldId = (name) => `auth-${name}`;

/**
 * Label + control + message. The message is rendered only when present:
 * reserving a line under all six fields cost more vertical space than the
 * layout shift it avoided.
 */
const Wrapper = ({ label, htmlFor, error, children }) => (
  <div className={`auth-field${error ? " auth-field--invalid" : ""}`}>
    {label && (
      <label className="auth-label" htmlFor={htmlFor}>
        {label}
      </label>
    )}

    {children}

    {error && (
      <span className="auth-field-error" role="alert">
        {error}
      </span>
    )}
  </div>
);

/** Only show a problem once the user has left the field or tried to submit. */
const visibleError = (meta) => (meta.touched && meta.error ? meta.error : "");

export const TextField = memo(function TextField({ name, label, ...inputProps }) {
  const id = fieldId(name);

  return (
    <FastField name={name}>
      {({ field, meta }) => (
        <Wrapper label={label} htmlFor={id} error={visibleError(meta)}>
          <Input id={id} size="large" {...field} {...inputProps} />
        </Wrapper>
      )}
    </FastField>
  );
});

export const SelectField = memo(function SelectField({
  name,
  label,
  options,
  placeholder = "Select",
}) {
  const id = fieldId(name);

  return (
    <FastField name={name}>
      {({ field, form, meta }) => (
        <Wrapper label={label} htmlFor={id} error={visibleError(meta)}>
          <Select
            id={id}
            popupClassName="auth-select-dropdown"
            size="large"
            style={{ width: "100%" }}
            options={options}
            placeholder={placeholder}
            // antd hands back a value, not an event, so Formik's own
            // `field.onChange` cannot be used directly here.
            value={field.value || undefined}
            onChange={(value) => form.setFieldValue(name, value)}
            onBlur={() => form.setFieldTouched(name, true)}
          />
        </Wrapper>
      )}
    </FastField>
  );
});

export const DateField = memo(function DateField({ name, label, max }) {
  const id = fieldId(name);

  return (
    <FastField name={name}>
      {({ field, meta }) => (
        <Wrapper label={label} htmlFor={id} error={visibleError(meta)}>
          <input
            id={id}
            type="date"
            className="auth-native-input"
            max={max}
            {...field}
          />
        </Wrapper>
      )}
    </FastField>
  );
});

/**
 * The country half of a phone field.
 *
 * It is its own `FastField` rather than a read of `form.values.country` from
 * inside the number field: `FastField` only re-renders for its own name, so a
 * select driven by a sibling value would go visually stale the moment it was
 * changed.
 *
 * `setFieldValue` re-runs the whole schema, and the schema reads the country
 * from `this.parent`, so the number's error updates from this one call.
 */
const CountrySelect = memo(function CountrySelect({ options }) {
  return (
    <FastField name="country">
      {({ field, form }) => (
        <Select
          className="auth-dial"
          popupClassName="auth-select-dropdown"
          size="large"
          showSearch
          optionFilterProp="title"
          options={options}
          value={field.value}
          onChange={(value) => form.setFieldValue("country", value)}
          aria-label="Country dial code"
        />
      )}
    </FastField>
  );
});

/** Country + number, or a plain email box when the channel is email. */
export const ContactField = memo(function ContactField({
  name,
  label,
  isPhone,
  countryOptions,
  placeholder,
}) {
  const id = fieldId(name);

  return (
    <FastField name={name}>
      {({ field, meta }) => (
        <Wrapper label={label} htmlFor={id} error={visibleError(meta)}>
          <div className="auth-row">
            {isPhone && <CountrySelect options={countryOptions} />}

            <div className="auth-grow">
              <Input
                id={id}
                size="large"
                placeholder={placeholder}
                autoComplete={isPhone ? "tel" : "email"}
                inputMode={isPhone ? "tel" : "email"}
                enterKeyHint="next"
                {...field}
              />
            </div>
          </div>
        </Wrapper>
      )}
    </FastField>
  );
});

/** Brand-gradient submit. `loading` is the caller's request flag. */
export const SubmitButton = memo(function SubmitButton({
  loading,
  disabled,
  children,
}) {
  return (
    <div className="auth-actions">
      <Button
        className="auth-btn-primary"
        htmlType="submit"
        size="large"
        block
        loading={loading}
        disabled={disabled}
      >
        {children}
      </Button>
    </div>
  );
});
